import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type CitationSet = {
  id: string;
  name: string;
  style: string;
  rules: string;
  bibliography: string;
  is_default: boolean;
  updated_at: string;
};

export const listCitationSets = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("citation_sets")
      .select("id,name,style,rules,bibliography,is_default,updated_at")
      .order("is_default", { ascending: false })
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { items: (data ?? []) as CitationSet[] };
  });

const saveInput = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().min(2).max(120),
  style: z.string().trim().min(2).max(60).default("OSCOLA"),
  rules: z.string().trim().max(8000).default(""),
  bibliography: z.string().trim().max(20000).default(""),
  is_default: z.boolean().default(false),
});

export const saveCitationSet = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => saveInput.parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    if (data.is_default) {
      await supabase
        .from("citation_sets")
        .update({ is_default: false })
        .eq("user_id", userId)
        .eq("is_default", true);
    }
    const row = {
      user_id: userId,
      name: data.name,
      style: data.style,
      rules: data.rules,
      bibliography: data.bibliography,
      is_default: data.is_default,
    };
    if (data.id) {
      const { data: upd, error } = await supabase
        .from("citation_sets")
        .update(row)
        .eq("id", data.id)
        .eq("user_id", userId)
        .select("id,name,style,rules,bibliography,is_default,updated_at")
        .single();
      if (error) throw new Error(error.message);
      return { item: upd as CitationSet };
    }
    const { data: ins, error } = await supabase
      .from("citation_sets")
      .insert(row)
      .select("id,name,style,rules,bibliography,is_default,updated_at")
      .single();
    if (error) throw new Error(error.message);
    return { item: ins as CitationSet };
  });

export const deleteCitationSet = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("citation_sets")
      .delete()
      .eq("id", data.id)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
